"use client";

import { Box, Button, ClickAwayListener, Tooltip, Typography } from "@mui/material";
import React from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUserGuideStep } from "@/hooks/useUserGuideStep";
import { useTranslations } from "next-intl";

interface OriginalStyles {
  body: {
    position: string;
    top: string;
    overflow: string;
    width: string;
    height: string;
  };
  html: {
    overflow: string;
  };
}

export default function NewUserGuide({ drawerOpen }: { drawerOpen?: boolean }) {
  const t = useTranslations("NewUserGuide");
  const STEPS = [
    { title: t("step1.title"), content: t("step1.content") },
    { title: t("step2.title"), content: t("step2.content") },
    { title: t("step3.title"), content: t("step3.content") },
    { title: t("step4.title"), content: t("step4.content") },
  ];
  const TOTAL_STEPS = STEPS.length;

  const [targets, setTargets] = React.useState<HTMLElement[]>([]);
  const [computedRect, setComputedRect] = React.useState<DOMRect | null>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { completed, setCompleted, writeCompleted, isLoading } = useUserGuideStep();

  // 跳过/关闭并标记完成
  const handleSkip = React.useCallback(() => {
    writeCompleted(TOTAL_STEPS);
    setCompleted(TOTAL_STEPS);
  }, [writeCompleted, setCompleted]);

  const retryRef = React.useRef(0);
  const lastRectRef = React.useRef<DOMRect | null>(null);

  const currentStep = Math.min(completed, TOTAL_STEPS - 1);
  const currentTarget = targets[currentStep] ?? null;
  // 仅在用户尚未完成引导且当前步骤有目标元素时显示引导
  const show = completed < TOTAL_STEPS && currentTarget !== null;
  const maskId = React.useMemo(() => `rp-guide-mask-${Math.random().toString(36).slice(2, 9)}`, []);

  const lockBodyScroll = React.useCallback(() => {
    if (typeof window === "undefined") return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    const html = document.documentElement;

    const originalStyles: OriginalStyles = {
      body: {
        position: body.style.position,
        top: body.style.top,
        overflow: body.style.overflow,
        width: body.style.width,
        height: body.style.height,
      },
      html: {
        overflow: html.style.overflow,
      },
    };

    (lockBodyScroll as any).__originalStyles = originalStyles;
    (lockBodyScroll as any).__scrollY = scrollY;

    try {
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.overflow = "hidden";
      body.style.width = "100%";
      body.style.height = "100%";
      html.style.overflow = "hidden";
    } catch (e) {
      console.warn("Failed to lock body scroll:", e);
    }
  }, [isSmallScreen]);

  const unlockBodyScroll = React.useCallback(() => {
    if (typeof window === "undefined") return;

    const originalStyles = (lockBodyScroll as any).__originalStyles;
    const scrollY = (lockBodyScroll as any).__scrollY || 0;

    if (!originalStyles) return;

    const body = document.body;
    const html = document.documentElement;

    try {
      body.style.position = originalStyles.body.position || "";
      body.style.top = originalStyles.body.top || "";
      body.style.overflow = originalStyles.body.overflow || "";
      body.style.width = originalStyles.body.width || "";
      body.style.height = originalStyles.body.height || "";
      html.style.overflow = originalStyles.html.overflow || "";

      window.scrollTo(0, scrollY);

      delete (lockBodyScroll as any).__originalStyles;
      delete (lockBodyScroll as any).__scrollY;
    } catch (e) {
      console.warn("Failed to unlock body scroll:", e);
    }
  }, [lockBodyScroll]);

  const findTargets = React.useCallback((): HTMLElement[] => {
    const selector = isSmallScreen ? ".user-guide-target-mobile" : ".user-guide-target-desktop";
    const explicit = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    if (explicit.length === 0) return [];

    const visibleExplicit = explicit.filter((el) => {
      try {
        const style = window.getComputedStyle(el);
        const r = el.getBoundingClientRect();
        return style.display !== "none" && style.visibility !== "hidden" && r.width > 0 && r.height > 0;
      } catch {
        return false;
      }
    });

    return visibleExplicit.slice(0, 4);
  }, []);

  // 之前的步骤已移除 — 导航简化为 下一步/跳过

  const handleNext = React.useCallback(async () => {
    if (currentStep >= TOTAL_STEPS - 1) {
      setCompleted(TOTAL_STEPS);
      await writeCompleted(TOTAL_STEPS);
    } else {
      const nextCompleted = currentStep + 1;
      setCompleted(nextCompleted);
      await writeCompleted(nextCompleted);
    }
  }, [currentStep, TOTAL_STEPS, writeCompleted, setCompleted]);

  // 计算矩形（带回退）
  const syncRect = currentTarget ? currentTarget.getBoundingClientRect() : null;
  const rect: DOMRect | null =
    computedRect ?? lastRectRef.current ?? (syncRect && syncRect.width > 0 && syncRect.height > 0 ? syncRect : null);
  const useRect = rect ?? new DOMRect(window.innerWidth / 2 - 100, 80, 200, 40);

  // 计算高亮区域（响应式内边距）：在小屏幕上使用更小的间距，使高亮区域更贴合文字
  const horizontalPadding = isSmallScreen ? 12 : 0; // px

  const rawLeft = useRect.left - horizontalPadding;
  const rawTop = useRect.top;
  const rawW = useRect.width + horizontalPadding * 2;
  const rawH = useRect.height;

  // Clamp to viewport
  const holeX = Math.max(0, Math.min(window.innerWidth - 4, rawLeft));
  const holeY = Math.max(0, Math.min(window.innerHeight - 4, rawTop));
  const holeW = Math.max(8, rawW);
  const holeH = Math.max(8, rawH);
  const radius = Math.min(holeH / 2, holeW / 2);

  const totalSteps = Math.max(1, Math.min(STEPS.length, targets.length));

  // 滚动锁定效果
  React.useEffect(() => {
    if (show) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    return unlockBodyScroll;
  }, [show, lockBodyScroll, unlockBodyScroll]);

  // 初始化目标元素
  React.useEffect(() => {
    if (isLoading || completed >= TOTAL_STEPS) return;

    // 在小屏幕上，除非 Drawer 打开，否则不尝试查找 Drawer 中的目标元素
    if (isSmallScreen && !drawerOpen) {
      setTargets([]);
      return;
    }

    const found = findTargets();
    setTargets(found);

    // 带防抖的 resize/scroll 处理器，以避免在快速调整窗口大小时频繁重新计算
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedOnResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setTargets(findTargets());
        resizeTimer = null;
      }, 100);
    };

    window.addEventListener("resize", debouncedOnResize);
    window.addEventListener("scroll", debouncedOnResize, true);

    return () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener("resize", debouncedOnResize);
      window.removeEventListener("scroll", debouncedOnResize, true);
    };
  }, [isLoading, completed, TOTAL_STEPS, findTargets]);

  // 当 Drawer 打开（移动端）时重新计算目标元素并触发引导显示检查
  React.useEffect(() => {
    if (drawerOpen && !isLoading && completed < TOTAL_STEPS) {
      // 短延迟以等待 Drawer 内容渲染完成
      const id = setTimeout(() => {
        const found = findTargets();
        setTargets(found);
        // Reset computedRect to force recomputation of target position
        setComputedRect(null);
      }, 300);

      return () => clearTimeout(id);
    }
  }, [drawerOpen, isLoading, completed, TOTAL_STEPS, findTargets]);

  // 如果当前目标变为不可见（例如在调整大小时 Drawer 被响应式 CSS 隐藏），
  // 重新计算目标元素，如果当前步骤没有可见的目标则隐藏引导。
  React.useEffect(() => {
    const checkVisibility = () => {
      if (!currentTarget) return;
      try {
        const style = window.getComputedStyle(currentTarget);
        const r = currentTarget.getBoundingClientRect();
        const visible = style.display !== "none" && style.visibility !== "hidden" && r.width > 0 && r.height > 0;
        if (!visible) {
          const found = findTargets();
          setTargets(found);
          const newTarget = found[currentStep] ?? null;
          if (!newTarget) {
            // No visible target for current step: ensure mask/tooltip hidden
            setComputedRect(null);
            setTargets([]);
          }
        }
      } catch {
        // ignore
      }
    };

    // 带防抖的可见性检查，以避免在快速调整大小/滚动时发生抖动
    let visTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedCheck = () => {
      if (visTimer) clearTimeout(visTimer);
      visTimer = setTimeout(() => {
        checkVisibility();
        visTimer = null;
      }, 120);
    };

    debouncedCheck();
    window.addEventListener("resize", debouncedCheck);
    window.addEventListener("scroll", debouncedCheck, true);

    return () => {
      if (visTimer) clearTimeout(visTimer);
      window.removeEventListener("resize", debouncedCheck);
      window.removeEventListener("scroll", debouncedCheck, true);
    };
  }, [currentTarget, currentStep, findTargets, drawerOpen, isSmallScreen]);

  // 当屏幕从移动端扩大到桌面端时，移动 Drawer 的目标会被响应式 CSS 隐藏。
  // 立即清除引导以避免残留的遮罩/提示框。
  React.useEffect(() => {
    if (!isSmallScreen && targets.length > 0) {
      // Screen is now desktop-sized, but we might have mobile targets
      // Check if current targets are mobile targets by trying to find desktop ones
      const desktopTargets = Array.from(document.querySelectorAll(".user-guide-target-desktop")) as HTMLElement[];
      if (desktopTargets.length === 0) {
        // No desktop targets available, clear mobile guide
        setTargets([]);
        setComputedRect(null);
      } else {
        // Switch to desktop targets
        const found = findTargets();
        setTargets(found);
      }
    }
  }, [isSmallScreen, targets.length, findTargets]);

  // 计算目标矩形
  React.useEffect(() => {
    retryRef.current = 0;
    if (!currentTarget) return;

    const tryCompute = () => {
      if (!currentTarget) return;

      const r = currentTarget.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        setComputedRect(r);
        lastRectRef.current = r;
        return;
      }

      try {
        currentTarget.scrollIntoView({ block: "center", inline: "center", behavior: "auto" });
      } catch {}

      retryRef.current += 1;
      if (retryRef.current <= 3) {
        setTimeout(() => {
          const r2 = currentTarget.getBoundingClientRect();
          if (r2 && r2.width > 0 && r2.height > 0) {
            setComputedRect(r2);
            lastRectRef.current = r2;
          } else if (retryRef.current < 3) {
            tryCompute();
          }
        }, 80);
      }
    };

    tryCompute();

    const onWin = () => {
      retryRef.current = 0;
      tryCompute();
    };
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);

    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [currentTarget]);

  if (!show || isLoading) return null;

  return (
    <Box sx={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1400 }}>
      <Box sx={{ position: "fixed", inset: 0, pointerEvents: "auto" }}>
        <svg width="100%" height="100%" style={{ display: "block" }}>
          <defs>
            <mask id={maskId} x="0" y="0" width="100%" height="100%">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect x={holeX} y={holeY} width={holeW} height={holeH} rx={radius} ry={radius} fill="black" />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.6)"
            mask={`url(#${maskId})`}
            pointerEvents="visiblePainted"
          />
        </svg>
      </Box>

      <ClickAwayListener onClickAway={() => {}}>
        <Tooltip
          open={show}
          arrow
          disableHoverListener
          disableFocusListener
          disableTouchListener
          slotProps={{
            tooltip: {
              sx: { bgcolor: "background.paper", color: "text.primary", width: "21.375rem", p: "0.75rem 1.25rem 1rem" },
            },
            arrow: { sx: { color: "background.paper" } },
            popper: {
              anchorEl: currentTarget ?? undefined,
              placement: "bottom",
              modifiers: [
                { name: "offset", options: { offset: [0, 8] } },
                { name: "preventOverflow", options: { padding: 8 } },
                { name: "flip", options: { fallbackPlacements: ["top", "right", "left"] } },
              ],
              style: { zIndex: 1600 },
            },
          }}
          title={
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600 }}>
                  {STEPS[currentStep]?.title}
                </Typography>
                <Box
                  component="button"
                  onClick={handleSkip}
                  sx={{
                    background: "transparent",
                    border: "none",
                    color: "text.secondary",
                    cursor: "pointer",
                    fontSize: "var(--font-size-xs)",
                  }}>
                  {t("skip")}
                </Box>
              </Box>

              <Typography sx={{ mt: 1, fontSize: "var(--font-size-xs)" }}>{STEPS[currentStep]?.content}</Typography>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1 }}>
                <Typography sx={{ fontSize: "var(--font-size-xs)", color: "text.secondary" }}>
                  <Typography component="span" sx={{ fontSize: "var(--font-size-xs)", color: "text.primary" }}>
                    {currentStep + 1}
                  </Typography>{" "}
                  /{" "}{totalSteps}
                </Typography>
                <Button
                  variant="contained"
                  color="tertiary"
                  onClick={handleNext}
                  sx={{
                    p: "0.25rem 0.75rem",
                    fontSize: "var(--font-size-2xs)",
                    fontWeight: 600,
                  }}>
                  {currentStep >= TOTAL_STEPS - 1 ? t("letsGo") : t("next")}
                </Button>
              </Box>
            </Box>
          }>
          <span />
        </Tooltip>
      </ClickAwayListener>
    </Box>
  );
}
